/**
 * <ConfirmDialog> — destructive confirmation pattern.
 *
 * UI-SPEC §Copywriting Contract §Destructive actions:
 *   - Every destructive action MUST use ConfirmDialog with the destructive
 *     body + destructive CTA from the copy table. Substitutes {action} and
 *     {entity} into a parameterized template.
 *   - `title`, `action`, `entity`, and `dismissLabel` are REQUIRED
 *     `NonEmptyString` props — the build fails if any is omitted or empty.
 *   - On `variant="destructive"` the Cancel button is the DEFAULT-FOCUSED
 *     control (axe-clean per H8; prevents accidental Enter-key destruction),
 *     and the dialog is a `role="alertdialog"` so it is announced as one.
 *
 * Composes `<Dialog>` from 04b (which enforces the H8 title contract at the
 * type level). Confirm + Cancel buttons are laid out at the trailing edge
 * (logical end) via Dialog's footer slot — Radix handles RTL mirroring
 * automatically.
 *
 * Plan 07 will swap the literal English strings ("This will permanently…",
 * "Yes, {action}") for i18n keys `common.confirm.destructive_body` +
 * `common.confirm.destructive_cta`.
 */
import * as React from "react";
import { Dialog } from "../../primitives/Dialog";
import { Button } from "../../primitives/Button";
import { type NonEmptyString } from "../../types/NonEmptyString";

export type ConfirmDialogVariant = "default" | "destructive";

export interface ConfirmDialogProps {
  /** Controlled open state. */
  open: boolean;
  /** Controlled-open change handler. */
  onOpenChange: (open: boolean) => void;
  /**
   * REQUIRED — non-empty heading, e.g. `nonEmpty("Delete employee?")`.
   * Enforced at compile time via NonEmptyString brand.
   */
  title: NonEmptyString;
  /**
   * REQUIRED — verb phrase substituted into the destructive body + CTA.
   * e.g. `nonEmpty("delete")`, `nonEmpty("remove access")`.
   */
  action: NonEmptyString;
  /**
   * REQUIRED — the entity the action applies to, substituted into the
   * destructive body, e.g. `nonEmpty("Ada Lovelace")`, `nonEmpty("this leave request")`.
   */
  entity: NonEmptyString;
  /**
   * REQUIRED — label for the Cancel button (no hardcoded default per UI-SPEC
   * secondary-CTA contract). e.g. `nonEmpty("Keep employee")`,
   * `nonEmpty("Go back")`.
   */
  dismissLabel: NonEmptyString;
  /** Variant — `destructive` is the common case; `default` for non-destructive confirm. */
  variant?: ConfirmDialogVariant;
  /** Fires when the user confirms. Closing on confirm is the caller's responsibility. */
  onConfirm: () => void;
  /**
   * Optional confirm-button CTA override. When omitted, destructive variant
   * uses `Yes, {action}` and default variant uses `Confirm`.
   */
  confirmLabel?: NonEmptyString;
  /**
   * Optional body override. When omitted and variant is destructive, the
   * body renders `This will permanently {action} {entity}. This cannot be
   * undone.` Consumers can pass a localized string + children if they need
   * richer body content.
   */
  body?: React.ReactNode;
  /**
   * Disables the confirm CTA while a submission is in flight (or a
   * precondition is unmet). Cancel stays enabled — the user can always back
   * out; only the committing action locks.
   */
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  action,
  // `entity` stays in the contract (consumers pass it and interpolate it into
  // their own translated `body`) but the DS no longer renders a default body,
  // so it is intentionally not consumed here.
  dismissLabel,
  variant = "destructive",
  onConfirm,
  confirmLabel,
  body,
  confirmDisabled = false,
}: ConfirmDialogProps) {
  const isDestructive = variant === "destructive";
  // No English default body — the DS is i18n-agnostic and must never emit a
  // hardcoded English sentence. Consumers that want a warning body pass a
  // translated `body`; otherwise the title + destructive styling + CTA carry it.
  const resolvedBody: React.ReactNode = body ?? null;
  // `action` is a consumer-provided (already-translated) NonEmptyString, so the
  // default CTA reuses it directly for BOTH variants. The old defaults injected
  // English — `Yes, ${action}` spliced "Yes, " before a translated verb, and the
  // non-destructive `"Confirm"` was English outright — so Arabic admins saw
  // English/mixed buttons on payroll finalize/hold/remove dialogs. Consumers
  // that want a fuller label still pass `confirmLabel`.
  const resolvedConfirmLabel: NonEmptyString = confirmLabel ?? action;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      {...(isDestructive ? { role: "alertdialog" as const } : {})}
      {...(typeof resolvedBody === "string" ? { description: resolvedBody } : {})}
      footer={
        <>
          {/*
           * Cancel is the default-focused control on destructive variant.
           * UI-SPEC §Destructive actions: "Cancel is always the
           * default-focused control on a destructive ConfirmDialog"
           * (axe-clean per H8; prevents accidental Enter-key destruction).
           * This is the *safer* choice — auto-focusing Cancel keeps the user
           * on a non-destructive action by default; they must deliberately
           * move to Confirm. Lint-disable is a deliberate a11y-positive.
           */}
          <Button
            variant="secondary"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={isDestructive}
            onClick={() => onOpenChange(false)}
          >
            {dismissLabel}
          </Button>
          <Button
            variant={isDestructive ? "destructive" : "primary"}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {resolvedConfirmLabel}
          </Button>
        </>
      }
    >
      {typeof resolvedBody !== "string" && resolvedBody}
    </Dialog>
  );
}
